import { JSX, Suspense, useEffect } from "react";

import { Box } from "@mui/material";
import Container from "@mui/material/Container";
import {
  CommonErrorBoundary,
  DateLocalizationProvider,
  LocalizationProvider,
  OAuthManager,
} from "@plansoft-configuration-renderer/components";
import { GatewayProvider } from "@plansoft-configuration-renderer/gateway";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import i18n from "./localisation/i18n";
import { CfgObjectSelect } from "./renderer/CfgObjectSelect";
import { MinimalAppContext } from "./renderer/MinimalAppContext";
import { MinimalRenderer } from "./renderer/MinimalRenderer";
import { Header } from "./renderer/components/Header/Header";
import { theme } from "./theme/theme";

const queryClient = new QueryClient();

// see vite.config.ts for the proxy configuration
const CONFIGURATOR_BASE_URL = "/preview";
const LANGUAGE =
  new URLSearchParams(window.location.search).get("lang") || "de-DE";

export function App(): JSX.Element {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);

  const cfgObjectId = urlParams.get("cfgObjectId");
  const sessionId = urlParams.get("sessionId");
  const currencyId = urlParams.get("currencyId");

  useEffect(() => {
    prettyLogger(
      `cpq-Standard-FE v${import.meta.env.PACKAGE_VERSION}`,
      theme.palette.primary.main,
    );

    i18n.changeLanguage(LANGUAGE);
  }, []);

  if (!cfgObjectId && !sessionId) {
    return <CfgObjectSelect />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <MinimalAppContext>
        <CommonErrorBoundary>
          <Header />
          <Container
            maxWidth="xl"
            sx={{
              paddingBottom: "5rem", //for the footer clearance
            }}
          >
            <Suspense>
              <LocalizationProvider
                configurationLocale={LANGUAGE}
                uiLocale={LANGUAGE}
                configuratorBasePath={CONFIGURATOR_BASE_URL}
              >
                <DateLocalizationProvider localeCode={LANGUAGE}>
                  <GatewayProvider>
                    <Box>
                      <MinimalRenderer
                        baseUrl={CONFIGURATOR_BASE_URL}
                        cfgObjectId={cfgObjectId}
                        sessionId={sessionId}
                        language={LANGUAGE}
                        currencyId={currencyId}
                      />

                      <OAuthManager
                        basePath={CONFIGURATOR_BASE_URL}
                        isLocalDevelopment={import.meta.env.DEV}
                      />
                    </Box>
                  </GatewayProvider>
                </DateLocalizationProvider>
              </LocalizationProvider>
            </Suspense>
          </Container>
        </CommonErrorBoundary>
      </MinimalAppContext>
    </QueryClientProvider>
  );
}

export function prettyLogger(text: string, color: string = "blue"): void {
  const styles = [
    "color: white",
    `background-color: ${color}`,
    "font-size: 12px",
    "padding: 20px",
    "border-radius: 5px",
  ].join(";");

  console.log(`%c ${text}`, styles);
}

export function dev(): void {
  window.debugMode = true;
}

window.dev = dev;

declare global {
  interface Window {
    debugMode?: boolean;
    dev: () => void;
  }
}
